/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — Updates (Phase 3).

   The office master moved; this panel says what that means for the
   client version on screen, one card per changed block:

     · the block's place in the document and the change itself, rendered
       with the SAME word-level diff the Track Changes view uses
     · what this client had already done to that block (their own
       wording, or left out) — the reason the decision exists
     · Accept  = take the office master's text
       Reject  = keep this client's version deviating, knowingly

   Header actions answer a whole publish at once. Nothing here is a
   destructive one-way door: a rejected update is an ordinary override,
   editable afterwards like any other text.

   Phase 6 puts MODULE updates in the same panel. A module definition
   moved, so every copy of it — this master's own core content, or a
   client version's own insertion — gets the same card treatment:
   "before" is what THIS copy says today, deviations included. Those are
   the one thing worth reviewing on the Office Master, so the panel is
   offered there too and lists module updates only.

   ⛔ HARD WALL: no update ever reaches a project spec.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { DiffToken } from '../../shared/block-diff';
import { diffWords } from '../../shared/block-diff';
import type { VariantChange, VariantChangeEntry } from '../../shared/variant-updates';
import { isModuleEntry, pendingCount } from '../../shared/variant-updates';
import EmptyState from '../components/EmptyState';

const UpdatesIcon = (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4 6h9M4 12h6M4 18h9M17 4v10M17 20v-2M13 8l4-4 4 4" />
  </svg>
);

const KIND_LABEL: Record<VariantChange['kind'], string> = {
  added: 'New block',
  removed: 'Block removed',
  modified: 'Text changed',
};

/** What the client had done to this block — the reason this is a decision
 *  and not just news. */
function deviationLine(change: VariantChange, clientName: string): string {
  if (change.hadHide) return `${clientName} leaves this block out — accepted automatically.`;
  if (change.hadOverride) return `${clientName} has their own wording on this block.`;
  if (change.kind === 'removed') return `${clientName} has no changes of their own here.`;
  if (change.kind === 'added') return `Held out of ${clientName}'s version until accepted.`;
  return `${clientName} is held at the previous text until this is accepted.`;
}

/** The same line for a MODULE update, where the subject is this copy. */
function moduleLine(change: VariantChange, subject: string): string {
  if (change.hadOverride) return `${subject} has its own wording here — accepting replaces it.`;
  if (change.kind === 'added') return `A new block in the module. ${subject} does not have it yet.`;
  if (change.kind === 'removed') return `The module no longer carries this block.`;
  return `${subject} still matches the module here.`;
}

function acceptTitle(change: VariantChange, isModule: boolean): string {
  if (isModule) {
    if (change.kind === 'removed') return 'Delete this block from this copy';
    if (change.kind === 'added') return 'Add the new block to this copy';
    return "Take the module's new text here";
  }
  if (change.kind === 'removed') return 'Let the block go for this client too';
  if (change.hadOverride) return "Drop this client's wording and take the office master text";
  return 'Take the office master text';
}

function rejectTitle(change: VariantChange, clientName: string, isModule: boolean): string {
  if (isModule) return 'Leave this copy exactly as it is';
  if (change.kind === 'removed') return `Keep the block for ${clientName}`;
  if (change.kind === 'added') return `Leave the new block out for ${clientName}`;
  if (change.hadOverride) return `Keep ${clientName}'s wording and stop flagging it`;
  return `Keep the previous text for ${clientName}`;
}

function DiffLine({ change }: { change: VariantChange }) {
  const before = change.beforeBlock?.text || '';
  const after = change.afterBlock?.text || '';

  if (change.kind === 'added') {
    return (
      <p className="sps-upd-line sps-diff-added">
        <span className="sps-tok sps-tok-add">{after || <em>(empty block)</em>}</span>
      </p>
    );
  }
  if (change.kind === 'removed') {
    return (
      <p className="sps-upd-line sps-diff-removed">
        <span className="sps-tok sps-tok-del">{before || <em>(empty block)</em>}</span>
      </p>
    );
  }
  const tokens: DiffToken[] = diffWords(before, after);
  return (
    <p className="sps-upd-line sps-diff-modified">
      {tokens.map((t, i) => (
        <span key={i} className={`sps-tok sps-tok-${t.op}`}>
          {t.value}
        </span>
      ))}
    </p>
  );
}

function ChangeCard({
  change,
  clientName,
  isModule,
  canDecide,
  readOnlyCards,
  busy,
  focused,
  onDecide,
}: {
  change: VariantChange;
  clientName: string;
  /** A module-definition update rather than a publish — different semantics,
   *  different words on every button. */
  isModule: boolean;
  canDecide: boolean;
  /** This copy can only answer as a whole (an insert made before per-block
   *  traceback existed) — the cards are the preview, the entry's two buttons
   *  are the answer. */
  readOnlyCards?: boolean;
  busy: boolean;
  focused: boolean;
  onDecide: (decision: 'accepted' | 'rejected') => void;
}) {
  const ref = React.useRef<HTMLLIElement>(null);
  React.useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [focused]);

  const settled = change.decision !== 'pending';
  const noPermTitle = 'Ask an admin for “Accept / Reject Master Updates” to decide this';

  return (
    <li
      ref={ref}
      className={`sps-upd-card${focused ? ' is-focused' : ''}${settled ? ' is-settled' : ''}`}
      data-change-id={change.changeId}
    >
      <div className="sps-upd-card-head">
        <span className={`sps-tag sps-tag-upd-${change.kind}`}>{KIND_LABEL[change.kind]}</span>
        {change.ref && <span className="sps-upd-ref">{change.ref}</span>}
        {settled && (
          <span className="sps-upd-state">
            {change.decision === 'accepted' ? 'Accepted' : 'Rejected'}
          </span>
        )}
      </div>

      <DiffLine change={change} />

      <p className="sps-upd-note">
        {change.note ||
          (isModule ? moduleLine(change, clientName) : deviationLine(change, clientName))}
      </p>

      {!settled && !readOnlyCards && (
        <div className="sps-upd-actions">
          <button
            type="button"
            className="dsg-btn"
            disabled={!canDecide || busy}
            title={canDecide ? rejectTitle(change, clientName, isModule) : noPermTitle}
            onClick={() => onDecide('rejected')}
          >
            Reject
          </button>
          <button
            type="button"
            className="dsg-primary"
            disabled={!canDecide || busy}
            title={canDecide ? acceptTitle(change, isModule) : noPermTitle}
            onClick={() => onDecide('accepted')}
          >
            Accept
          </button>
        </div>
      )}
    </li>
  );
}

export interface UpdatesPanelProps {
  /** Null on the Office Master — the feed is about a client version. */
  clientName: string | null;
  entries: VariantChangeEntry[];
  canDecide: boolean;
  busy: boolean;
  focusChangeId: string | null;
  onDecide: (entryId: string, changeId: string, decision: 'accepted' | 'rejected') => void;
  onDecideAll: (entryId: string, decision: 'accepted' | 'rejected') => void;
  /** A header line above the feed (the dock panel's "what is this"). */
  intro?: React.ReactNode;
}

export function UpdatesPanel({
  clientName,
  entries,
  canDecide,
  busy,
  focusChangeId,
  onDecide,
  onDecideAll,
  intro,
}: UpdatesPanelProps) {
  const [showSettled, setShowSettled] = React.useState(false);

  // On the Office Master the panel is about MODULE updates — a publish means
  // nothing to the document it came from. `entries` is already scoped to that
  // by the hook; the copy just has to say which world you are in.
  const onMaster = !clientName;
  const subject = clientName || 'This master';

  const open = entries.filter((e) => pendingCount(e) > 0);
  const settled = entries.filter((e) => pendingCount(e) === 0);
  const shown = showSettled ? entries : open;

  return (
    <div className="dsg-pal sps-pal sps-upd-pal">
      {intro}
      {entries.length === 0 ? (
        <EmptyState
          icon={UpdatesIcon}
          title="Nothing to review"
          subtitle={
            onMaster
              ? 'This is the office master. Module updates land here when a module it uses is updated; pick a client above to see what a publish means for their copy.'
              : `${clientName}'s version is up to date with the office master.`
          }
        />
      ) : (
        <>
          {settled.length > 0 && (
            <div className="sps-upd-filter">
              <label className="sps-upd-toggle">
                <input
                  type="checkbox"
                  checked={showSettled}
                  onChange={(e) => setShowSettled(e.target.checked)}
                />
                Show settled updates ({settled.length})
              </label>
            </div>
          )}

          {shown.length === 0 ? (
            <EmptyState
              icon={UpdatesIcon}
              title="Nothing waiting"
              subtitle={
                onMaster
                  ? 'Every module update to this master has been answered.'
                  : `Every update to ${clientName}'s version has been answered.`
              }
            />
          ) : (
            shown.map((entry) => {
              const waiting = pendingCount(entry);
              const isModule = isModuleEntry(entry);
              const from = isModule ? entry.moduleFromVersion : entry.fromVersion;
              const to = isModule ? entry.moduleToVersion : entry.toVersion;
              // R2 Phase 3: a LOCAL update — the library moved ahead of a
              // module standing in this document. Same cards, same words; the
              // bulk pair says what it actually does to this copy.
              const isLocal = !!entry.local;
              const wholeOnly = !!entry.wholeModuleOnly;
              const rejectAllLabel = isLocal
                ? wholeOnly
                  ? 'Keep mine'
                  : 'Keep all mine'
                : 'Reject all';
              const acceptAllLabel = isLocal && wholeOnly ? 'Take update' : 'Accept all';
              return (
                <section
                  key={entry.id}
                  className="sps-upd-entry"
                  aria-label={isModule ? 'Module update' : 'Update from publish'}
                >
                  <header className="sps-upd-entry-head">
                    <h3 className="sps-pal-head">
                      {isModule
                        ? `Module update: ${entry.moduleName || 'Module'}`
                        : 'Update from Office Master'}
                      <span className="sps-upd-ver">
                        v{from} → v{to}
                      </span>
                    </h3>
                    <p className="sps-pal-hint">
                      {waiting > 0
                        ? isLocal
                          ? `${waiting} paragraph${waiting === 1 ? '' : 's'} differ${
                              waiting === 1 ? 's' : ''
                            } from the library`
                          : `${waiting} change${waiting === 1 ? '' : 's'} waiting for ${
                              onMaster ? 'this master' : clientName
                            }`
                        : 'All answered'}
                    </p>
                    {wholeOnly && (
                      <p className="sps-pal-hint">
                        This copy was inserted before paragraph-level tracking, so it can only be
                        taken or kept as a whole.
                      </p>
                    )}
                    {waiting > 0 && (
                      <div className="sps-upd-actions">
                        <button
                          type="button"
                          className="dsg-btn"
                          disabled={!canDecide || busy}
                          title={
                            canDecide
                              ? isLocal
                                ? 'Keep this copy as it is — the next version of the module will ask again'
                                : isModule
                                  ? 'Leave this copy exactly as it is for every change here'
                                  : `Keep ${clientName}'s version as it stands for every change here`
                              : 'Ask an admin for “Accept / Reject Master Updates” to decide these'
                          }
                          onClick={() => onDecideAll(entry.id, 'rejected')}
                        >
                          {rejectAllLabel}
                        </button>
                        <button
                          type="button"
                          className="dsg-primary"
                          disabled={!canDecide || busy}
                          title={
                            canDecide
                              ? isModule
                                ? 'Take the module’s new version for every change here'
                                : 'Take the office master text for every change here'
                              : 'Ask an admin for “Accept / Reject Master Updates” to decide these'
                          }
                          onClick={() => onDecideAll(entry.id, 'accepted')}
                        >
                          {acceptAllLabel}
                        </button>
                      </div>
                    )}
                  </header>

                  <ul className="sps-upd-list">
                    {(entry.changes || []).map((change) => (
                      <ChangeCard
                        key={change.changeId}
                        change={change}
                        clientName={subject}
                        isModule={isModule}
                        canDecide={canDecide}
                        readOnlyCards={wholeOnly}
                        busy={busy}
                        focused={focusChangeId === change.changeId}
                        onDecide={(decision) => onDecide(entry.id, change.changeId, decision)}
                      />
                    ))}
                  </ul>
                </section>
              );
            })
          )}
        </>
      )}
    </div>
  );
}
